import ErrorMessage from '@/components/common/error-message/ErrorMessage';
import { GenericTable } from '@/components/common/tables/common/generic-table/GenericTable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TServiceDivision } from '@/services/servicesDivision';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { PenBoxIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
const ServiceDivision: React.FC<{
  serviceDivision: TServiceDivision[] | undefined;
  isLoading: boolean;
  isError: boolean;
}> = ({ serviceDivision, isLoading, isError }) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<TServiceDivision>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('common.name')
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: t('form.description')
      },
      {
        id: 'duration',
        accessorKey: 'duration',
        header: t('form.duration')
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: t('form.quantity')
      },
      {
        id: 'actions',
        accessorKey: 'actions',
        header: t('common.actions.title'),
        cell: ({ row }) => {
          return (
            <div className="flex gap-2">
              <Button variant="link" size="icon" className="w-8 h-8" asChild>
                <Link to={`./edit-division/${row.original.id}`}>
                  <PenBoxIcon className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          );
        }
      }
    ],
    [t]
  );

  const table = useReactTable({
    data: serviceDivision ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    enableMultiSort: false,
    getSortedRowModel: getSortedRowModel()
  });

  if (isLoading) {
    return (
      <div className="w-full lg:w-[20rem] bg-accent p-4 rounded-md">
        <Skeleton className="w-full h-[15rem]" />
      </div>
    );
  }

  if (isError) {
    return <ErrorMessage />;
  }

  if (!serviceDivision) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col justify-between gap-4 md:flex-row">
        <p className="font-bold ">
          {t('serviceDetail.divisionConfiguration.serviceDivision')}
        </p>
        <Button asChild>
          <Link to={`./create-division`}>
            {t('serviceDetail.createServiceDivision.title')}
          </Link>
        </Button>
      </div>
      <GenericTable table={table} isLoading={isLoading} />
    </>
  );
};

export default ServiceDivision;
